import { Schema } from "effect";
import { BuildArtifactKey } from "../development/build-identity/model.ts";

const BuildSingleFlightCapacityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500) }))();
type BuildSingleFlightCapacityErrorProperties = typeof BuildSingleFlightCapacityErrorFields["~type.make.in"];
export const BuildSingleFlightCapacityError = /* @__PURE__ */ (() => {
  class BuildSingleFlightCapacityError extends Schema.TaggedError<BuildSingleFlightCapacityError>()("BuildSingleFlightCapacityError", BuildSingleFlightCapacityErrorFields) {
    constructor(message: string | BuildSingleFlightCapacityErrorProperties, options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "BuildSingleFlightCapacityError";
    }
    toJSON() { return { name: this.name }; }
  }
  return BuildSingleFlightCapacityError;
})();
export type BuildSingleFlightCapacityError = InstanceType<typeof BuildSingleFlightCapacityError>;

const CompilerWebSocketCapabilityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500) }))();
type CompilerWebSocketCapabilityErrorProperties = typeof CompilerWebSocketCapabilityErrorFields["~type.make.in"];
export const CompilerWebSocketCapabilityError = /* @__PURE__ */ (() => {
  class CompilerWebSocketCapabilityError extends Schema.TaggedError<CompilerWebSocketCapabilityError>()("CompilerWebSocketCapabilityError", CompilerWebSocketCapabilityErrorFields) {
    constructor(message: string | CompilerWebSocketCapabilityErrorProperties, options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "CompilerWebSocketCapabilityError";
    }
    toJSON() { return { name: this.name }; }
  }
  return CompilerWebSocketCapabilityError;
})();
export type CompilerWebSocketCapabilityError = InstanceType<typeof CompilerWebSocketCapabilityError>;

const CompilerWebSocketFailureFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: Schema.optional(Schema.String) }))();
type CompilerWebSocketFailureProperties = typeof CompilerWebSocketFailureFields["~type.make.in"];
export const CompilerWebSocketFailure = /* @__PURE__ */ (() => {
  class CompilerWebSocketFailure extends Schema.TaggedError<CompilerWebSocketFailure>()("CompilerWebSocketFailure", CompilerWebSocketFailureFields) {
    constructor(message: string, code?: string);
    constructor(properties: CompilerWebSocketFailureProperties, options?: Schema.MakeOptions);
    constructor(message: string | CompilerWebSocketFailureProperties, code?: string | Schema.MakeOptions) {
      super(typeof message === "string" ? { message, code: typeof code === "string" ? code : undefined } : message,
        typeof code === "object" ? code : undefined);
      this.name = "CompilerWebSocketFailure";
    }
    toJSON() { return { name: this.name, code: this.code }; }
  }
  return CompilerWebSocketFailure;
})();
export type CompilerWebSocketFailure = InstanceType<typeof CompilerWebSocketFailure>;

const ContainerSessionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: Schema.optional(Schema.String) }))();
type ContainerSessionErrorProperties = typeof ContainerSessionErrorFields["~type.make.in"];
export const ContainerSessionError = /* @__PURE__ */ (() => {
  class ContainerSessionError extends Schema.TaggedError<ContainerSessionError>()("ContainerSessionError", ContainerSessionErrorFields) {
    constructor(message: string, code?: string);
    constructor(properties: ContainerSessionErrorProperties, options?: Schema.MakeOptions);
    constructor(message: string | ContainerSessionErrorProperties, code?: string | Schema.MakeOptions) {
      super(typeof message === "string" ? { message, code: typeof code === "string" ? code : undefined } : message,
        typeof code === "object" ? code : undefined);
      this.name = "ContainerSessionError";
    }
    toJSON() { return { name: this.name, code: this.code }; }
  }
  return ContainerSessionError;
})();
export type ContainerSessionError = InstanceType<typeof ContainerSessionError>;

const DuplicateBuildWaiterErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.tag("A waiter identity may be used only once within a build flight."), status: Schema.tag(500) }))();
type DuplicateBuildWaiterErrorProperties = typeof DuplicateBuildWaiterErrorFields["~type.make.in"];
export const DuplicateBuildWaiterError = /* @__PURE__ */ (() => {
  class DuplicateBuildWaiterError extends Schema.TaggedError<DuplicateBuildWaiterError>()("DuplicateBuildWaiterError", DuplicateBuildWaiterErrorFields) {
    constructor(properties: DuplicateBuildWaiterErrorProperties = {}, options?: Schema.MakeOptions) {
      super(properties, options);
      this.name = "DuplicateBuildWaiterError";
    }
    toJSON() { return { name: this.name }; }
  }
  return DuplicateBuildWaiterError;
})();
export type DuplicateBuildWaiterError = InstanceType<typeof DuplicateBuildWaiterError>;

const PrepareCacheMissErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.tag("Artifact not cached; warm it before an execute-only measurement."), status: Schema.tag(409), cacheKey: Schema.String }))();
type PrepareCacheMissErrorProperties = typeof PrepareCacheMissErrorFields["~type.make.in"];
export const PrepareCacheMissError = /* @__PURE__ */ (() => {
  class PrepareCacheMissError extends Schema.TaggedError<PrepareCacheMissError>()("PrepareCacheMissError", PrepareCacheMissErrorFields) {
    constructor(cacheKey: string | PrepareCacheMissErrorProperties, options?: Schema.MakeOptions) {
      super(typeof cacheKey === "string" ? { cacheKey } : cacheKey, options);
      this.name = "PrepareCacheMissError";
    }
    toJSON() { return { cacheKey: this.cacheKey, name: this.name }; }
  }
  return PrepareCacheMissError;
})();
export type PrepareCacheMissError = InstanceType<typeof PrepareCacheMissError>;

const WorkspaceBuildFailureFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(400) }))();
type WorkspaceBuildFailureProperties = typeof WorkspaceBuildFailureFields["~type.make.in"];
/** The caller's workspace failed to build; the message is the bundler's public summary. */
export const WorkspaceBuildFailure = /* @__PURE__ */ (() => {
  class WorkspaceBuildFailure extends Schema.TaggedError<WorkspaceBuildFailure>()("WorkspaceBuildFailure", WorkspaceBuildFailureFields) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is WorkspaceBuildFailure =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(message: string | WorkspaceBuildFailureProperties, options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "WorkspaceBuildFailure";
    }
    toJSON() { return { name: this.name, message: this.message }; }
  }
  return WorkspaceBuildFailure;
})();
export type WorkspaceBuildFailure = InstanceType<typeof WorkspaceBuildFailure>;

export const BuildArtifactQuarantineReason = /* @__PURE__ */ (() => Schema.Literals([
  "invalid-schema", "key-mismatch", "invalid-digest", "invalid-byte-length", "invalid-content",
  "artifact-too-large", "byte-length-mismatch", "digest-mismatch", "provenance-mismatch"
]))();
export type BuildArtifactQuarantineReason = typeof BuildArtifactQuarantineReason.Type;
const ArtifactIntegrityReason = /* @__PURE__ */ (() => Schema.Union([BuildArtifactQuarantineReason, Schema.Literal("quarantine-failed")]))();
type ArtifactIntegrityReason = typeof ArtifactIntegrityReason.Type;

const BuildArtifactIntegrityErrorFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(500), buildKey: BuildArtifactKey,
  reason: ArtifactIntegrityReason, cause: Schema.optional(Schema.Unknown)
}))();
type BuildArtifactIntegrityErrorProperties = typeof BuildArtifactIntegrityErrorFields["~type.make.in"];
export const BuildArtifactIntegrityError = /* @__PURE__ */ (() => {
  class BuildArtifactIntegrityError extends Schema.TaggedError<BuildArtifactIntegrityError>()("BuildArtifactIntegrityError", BuildArtifactIntegrityErrorFields) {
    constructor(buildKey: BuildArtifactKey, reason: ArtifactIntegrityReason, cause?: unknown);
    constructor(properties: BuildArtifactIntegrityErrorProperties, options?: Schema.MakeOptions);
    constructor(buildKey: BuildArtifactKey | BuildArtifactIntegrityErrorProperties, reason?: ArtifactIntegrityReason | Schema.MakeOptions, cause?: unknown) {
      super(typeof buildKey === "string" ? {
        buildKey, reason: reason as ArtifactIntegrityReason, cause,
        message: `Build artifact ${buildKey} failed integrity verification: ${reason}.`
      } : buildKey, typeof reason === "object" ? reason : undefined);
      this.name = "BuildArtifactIntegrityError";
    }
    toJSON() { return { name: this.name, buildKey: this.buildKey, reason: this.reason, cause: this.cause }; }
  }
  return BuildArtifactIntegrityError;
})();
export type BuildArtifactIntegrityError = InstanceType<typeof BuildArtifactIntegrityError>;

const StoredSourceVersionStorageErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(503), cause: Schema.optional(Schema.Unknown) }))();
type StoredSourceVersionStorageErrorProperties = typeof StoredSourceVersionStorageErrorFields["~type.make.in"];
export const StoredSourceVersionStorageError = /* @__PURE__ */ (() => {
  class StoredSourceVersionStorageError extends Schema.TaggedError<StoredSourceVersionStorageError>()("StoredSourceVersionStorageError", StoredSourceVersionStorageErrorFields) {
    constructor(message: string, cause?: unknown);
    constructor(properties: StoredSourceVersionStorageErrorProperties, options?: Schema.MakeOptions);
    constructor(message: string | StoredSourceVersionStorageErrorProperties, cause?: unknown) {
      // The properties overload receives Schema's own construction options;
      // the positional overload retains the original opaque platform cause.
      super(typeof message === "string" ? { message, cause } : message,
        typeof message === "string" ? undefined : cause as Schema.MakeOptions | undefined);
      this.name = "StoredSourceVersionStorageError";
    }
    toJSON() { return { name: this.name, cause: this.cause }; }
  }
  return StoredSourceVersionStorageError;
})();
export type StoredSourceVersionStorageError = InstanceType<typeof StoredSourceVersionStorageError>;

const ResponseBodyLimitErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: Schema.tag("response-body-too-large"), limitBytes: Schema.Number }))();
type ResponseBodyLimitErrorProperties = typeof ResponseBodyLimitErrorFields["~type.make.in"];
export const ResponseBodyLimitError = /* @__PURE__ */ (() => {
  class ResponseBodyLimitError extends Schema.TaggedError<ResponseBodyLimitError>()("ResponseBodyLimitError", ResponseBodyLimitErrorFields) {
    constructor(limitBytes: number | ResponseBodyLimitErrorProperties, options?: Schema.MakeOptions) {
      super(typeof limitBytes === "number" ? { limitBytes, message: `Response body exceeds ${limitBytes} bytes.` } : limitBytes, options);
      this.name = "ResponseBodyLimitError";
    }
    toJSON() { return { code: this.code, name: this.name, limitBytes: this.limitBytes }; }
  }
  return ResponseBodyLimitError;
})();
export type ResponseBodyLimitError = InstanceType<typeof ResponseBodyLimitError>;

const OperationDeadlineErrorFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(500), code: Schema.tag("operation-timeout"),
  operation: Schema.String, phase: Schema.String, deadlineAt: Schema.Number
}))();
type OperationDeadlineErrorProperties = typeof OperationDeadlineErrorFields["~type.make.in"];
export const OperationDeadlineError = /* @__PURE__ */ (() => {
  class OperationDeadlineError extends Schema.TaggedError<OperationDeadlineError>()("OperationDeadlineError", OperationDeadlineErrorFields) {
    constructor(operation: string, phase: string, deadlineAt: number);
    constructor(properties: OperationDeadlineErrorProperties, options?: Schema.MakeOptions);
    constructor(operation: string | OperationDeadlineErrorProperties, phase?: string | Schema.MakeOptions, deadlineAt?: number) {
      super(typeof operation === "string" ? {
        operation, phase: phase as string, deadlineAt: deadlineAt as number,
        message: `${operation} timed out while waiting for ${phase}.`
      } : operation, typeof phase === "object" ? phase : undefined);
      this.name = "TimeoutError";
    }
    toJSON() { return { code: this.code, name: this.name, operation: this.operation, phase: this.phase, deadlineAt: this.deadlineAt }; }
  }
  return OperationDeadlineError;
})();
export type OperationDeadlineError = InstanceType<typeof OperationDeadlineError>;

export const PreparedDispatchReconstructionFailure = /* @__PURE__ */ (() => Schema.Literals(["artifact-missing", "artifact-invalid", "capability-stale", "runtime-invalid"]))();
export type PreparedDispatchReconstructionFailure = typeof PreparedDispatchReconstructionFailure.Type;

const PreparedDispatchReconstructionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(503), reason: PreparedDispatchReconstructionFailure }))();
type PreparedDispatchReconstructionErrorProperties = typeof PreparedDispatchReconstructionErrorFields["~type.make.in"];
export const PreparedDispatchReconstructionError = /* @__PURE__ */ (() => {
  class PreparedDispatchReconstructionError extends Schema.TaggedError<PreparedDispatchReconstructionError>()("PreparedDispatchReconstructionError", PreparedDispatchReconstructionErrorFields) {
    constructor(reason: PreparedDispatchReconstructionFailure | PreparedDispatchReconstructionErrorProperties, options?: Schema.MakeOptions) {
      super(typeof reason === "string" ? { reason, message: `Prepared Worker reconstruction failed (${reason}).` } : reason, options);
      this.name = "PreparedDispatchReconstructionError";
    }
    toJSON() { return { name: this.name, reason: this.reason }; }
  }
  return PreparedDispatchReconstructionError;
})();
export type PreparedDispatchReconstructionError = InstanceType<typeof PreparedDispatchReconstructionError>;

export const WorkspaceBuildArtifactIntegrityReason = /* @__PURE__ */ (() => Schema.Literals(["invalid-artifact", "unsupported-schema", "identity-mismatch", "digest-mismatch", "length-mismatch"]))();
export type WorkspaceBuildArtifactIntegrityReason = typeof WorkspaceBuildArtifactIntegrityReason.Type;

const WorkspaceBuildArtifactIntegrityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), reason: WorkspaceBuildArtifactIntegrityReason }))();
type WorkspaceBuildArtifactIntegrityErrorProperties = typeof WorkspaceBuildArtifactIntegrityErrorFields["~type.make.in"];
export const WorkspaceBuildArtifactIntegrityError = /* @__PURE__ */ (() => {
  class WorkspaceBuildArtifactIntegrityError extends Schema.TaggedError<WorkspaceBuildArtifactIntegrityError>()("WorkspaceBuildArtifactIntegrityError", WorkspaceBuildArtifactIntegrityErrorFields) {
    constructor(reason: WorkspaceBuildArtifactIntegrityReason | WorkspaceBuildArtifactIntegrityErrorProperties, options?: Schema.MakeOptions) {
      super(typeof reason === "string" ? { reason, message: `Cached build artifact failed validation (${reason}).` } : reason, options);
      this.name = "WorkspaceBuildArtifactIntegrityError";
    }
    toJSON() { return { name: this.name, reason: this.reason }; }
  }
  return WorkspaceBuildArtifactIntegrityError;
})();
export type WorkspaceBuildArtifactIntegrityError = InstanceType<typeof WorkspaceBuildArtifactIntegrityError>;

export const BrowserRestRefusal = /* @__PURE__ */ (() => Schema.Literals([
  "unconfigured", "unsupported-operation", "unknown-route", "upstream-unreachable", "gateway-rejected"
]))();
export type BrowserRestRefusal = typeof BrowserRestRefusal.Type;
const BROWSER_REFUSAL_STATUS = Object.freeze({
  unconfigured: 503, "unsupported-operation": 501, "unknown-route": 501,
  "upstream-unreachable": 502, "gateway-rejected": 503
});
const BrowserRestFailureFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, reason: BrowserRestRefusal, status: Schema.Literals([501, 502, 503])
}).check(Schema.makeFilter(value => value.status === BROWSER_REFUSAL_STATUS[value.reason])))();
type BrowserRestFailureProperties = typeof BrowserRestFailureFields["~type.make.in"];
export const BrowserRestUnavailable = /* @__PURE__ */ (() => {
  class BrowserRestUnavailable extends Schema.TaggedError<BrowserRestUnavailable>()("BrowserRestUnavailable", BrowserRestFailureFields) {
    constructor(reason: BrowserRestRefusal, message: string);
    constructor(properties: BrowserRestFailureProperties, options?: Schema.MakeOptions);
    constructor(reason: BrowserRestRefusal | BrowserRestFailureProperties, message?: string | Schema.MakeOptions) {
      super(typeof reason === "string" ? { reason, message: typeof message === "string" ? message : "", status: BROWSER_REFUSAL_STATUS[reason] } : reason,
        typeof message === "object" ? message : undefined);
      this.name = "BrowserRestUnavailable";
    }
    toJSON() { return { name: this.name, reason: this.reason }; }
  }
  return BrowserRestUnavailable;
})();
export type BrowserRestUnavailable = InstanceType<typeof BrowserRestUnavailable>;

const BuildArtifactTooLargeErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(400), byteLength: Schema.Number, maxBytes: Schema.Number }))();
type BuildArtifactTooLargeErrorProperties = typeof BuildArtifactTooLargeErrorFields["~type.make.in"];
export const BuildArtifactTooLargeError = /* @__PURE__ */ (() => {
  class BuildArtifactTooLargeError extends Schema.TaggedError<BuildArtifactTooLargeError>()("BuildArtifactTooLargeError", BuildArtifactTooLargeErrorFields) {
    constructor(byteLength: number, maxBytes: number);
    constructor(properties: BuildArtifactTooLargeErrorProperties, options?: Schema.MakeOptions);
    constructor(byteLength: number | BuildArtifactTooLargeErrorProperties, maxBytes?: number | Schema.MakeOptions) {
      super(typeof byteLength === "number" ? { byteLength, maxBytes: maxBytes as number, message: `Build artifact is ${byteLength} bytes; the limit is ${maxBytes}.` } : byteLength, typeof maxBytes === "object" ? maxBytes : undefined);
      this.name = "BuildArtifactTooLargeError";
    }
    toJSON() { return { name: this.name, byteLength: this.byteLength, maxBytes: this.maxBytes }; }
  }
  return BuildArtifactTooLargeError;
})();
export type BuildArtifactTooLargeError = InstanceType<typeof BuildArtifactTooLargeError>;

const CompilerConfigurationErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(400), field: Schema.optional(Schema.String) }))();
type CompilerConfigurationErrorProperties = typeof CompilerConfigurationErrorFields["~type.make.in"];
export const CompilerConfigurationError = /* @__PURE__ */ (() => {
  class CompilerConfigurationError extends Schema.TaggedError<CompilerConfigurationError>()("CompilerConfigurationError", CompilerConfigurationErrorFields) {
    constructor(message: string, field?: string);
    constructor(properties: CompilerConfigurationErrorProperties, options?: Schema.MakeOptions);
    constructor(message: string | CompilerConfigurationErrorProperties, field?: string | Schema.MakeOptions) {
      super(typeof message === "string" ? { message, field: typeof field === "string" ? field : undefined } : message, typeof field === "object" ? field : undefined);
      this.name = "CompilerConfigurationError";
    }
    toJSON() { return { name: this.name, field: this.field }; }
  }
  return CompilerConfigurationError;
})();
export type CompilerConfigurationError = InstanceType<typeof CompilerConfigurationError>;

export const RuntimeFailure = /* @__PURE__ */ (() => Schema.Union([
  BuildArtifactTooLargeError, CompilerConfigurationError, BuildSingleFlightCapacityError, CompilerWebSocketCapabilityError, CompilerWebSocketFailure, ContainerSessionError, DuplicateBuildWaiterError, PrepareCacheMissError, WorkspaceBuildFailure, BuildArtifactIntegrityError, StoredSourceVersionStorageError, ResponseBodyLimitError, OperationDeadlineError, PreparedDispatchReconstructionError, WorkspaceBuildArtifactIntegrityError, BrowserRestUnavailable
]))();
export type RuntimeFailure = typeof RuntimeFailure.Type;
